import * as tslib_1 from "tslib";
/** @jsx jsx */

import { jsx, css } from "@emotion/core";
import * as React from "react";
import { useTransition, animated } from "react-spring";
import { useFocusElement } from "./Hooks/focus";
import { Overlay } from "./Overlay";
import PropTypes from "prop-types";
import { RemoveScroll } from "react-remove-scroll";
import { useTheme } from "./Theme/Providers";
export var RequestCloseContext = React.createContext(function () {});
var animationConfig = {
  mass: 1,
  tension: 185,
  friction: 26
};

function getTransitionForPosition(position) {
  switch (position) {
    case "left":
      return {
        from: {
          transform: "translate3d(-100%, 0, 0)"
        },
        enter: {
          transform: "translate3d(0, 0, 0)"
        },
        leave: {
          transform: "translate3d(-100%, 0, 0)"
        },
        config: animationConfig
      };

    case "right":
      return {
        from: {
          transform: "translate3d(100%, 0, 0)"
        },
        enter: {
          transform: "translate3d(0, 0, 0)"
        },
        leave: {
          transform: "translate3d(100%, 0, 0)"
        },
        config: animationConfig
      };

    case "top":
      return {
        from: {
          transform: "translateY(-100%)"
        },
        enter: {
          transform: "translateY(0)"
        },
        leave: {
          transform: "translateY(-100%)"
        },
        config: animationConfig
      };

    case "bottom":
      return {
        from: {
          transform: "translateY(100%)"
        },
        enter: {
          transform: "translateY(0)"
        },
        leave: {
          transform: "translateY(100%)"
        },
        config: animationConfig
      };
  }
}

var positions = function positions(theme) {
  var _a, _b, _c, _d;

  return {
    left: css((_a = {
      top: 0,
      left: 0,
      bottom: 0,
      width: "auto",
      maxWidth: "100vw"
    }, _a[theme.mediaQueries.md] = {
      maxWidth: "400px"
    }, _a)),
    right: css((_b = {
      top: 0,
      right: 0,
      bottom: 0,
      width: "auto",
      maxWidth: "100vw"
    }, _b[theme.mediaQueries.md] = {
      maxWidth: "400px"
    }, _b)),
    bottom: css((_c = {
      bottom: 0,
      left: 0,
      right: 0,
      height: "auto",
      width: "100%",
      padding: 0,
      boxSizing: "border-box"
    }, _c[theme.mediaQueries.md] = {
      maxHeight: "400px"
    }, _c["& > div"] = {
      borderTopRightRadius: theme.radii.lg,
      borderTopLeftRadius: theme.radii.lg,
      paddingBottom: theme.spaces.lg,
      paddingTop: theme.spaces.xs
    }, _c)),
    top: css((_d = {
      top: 0,
      left: 0,
      right: 0,
      height: "auto",
      width: "100%",
      padding: 0
    }, _d[theme.mediaQueries.md] = {
      maxHeight: "400px"
    }, _d["& > div"] = {
      borderBottomRightRadius: theme.radii.lg,
      borderBottomLeftRadius: theme.radii.lg,
      paddingBottom: theme.spaces.xs,
      paddingTop: theme.spaces.md
    }, _d))
  };
};

var noop = function noop() {};
/**
 * A sheet is useful for displaying app based navigation (typically on the left),
 * supplemental information (on the right), or menu options (typically
 * from the bottom on mobile devices).
 *
 * Sheets should not be tied to specific URLs.
 */


export var Sheet = function Sheet(_a) {
  var isOpen = _a.isOpen,
      children = _a.children,
      _b = _a.role,
      role = _b === void 0 ? "document" : _b,
      _c = _a.closeOnClick,
      closeOnClick = _c === void 0 ? true : _c,
      _d = _a.position,
      position = _d === void 0 ? "right" : _d,
      onRequestClose = _a.onRequestClose,
      props = tslib_1.__rest(_a, ["isOpen", "children", "role", "closeOnClick", "position", "onRequestClose"]);

  var theme = useTheme();
  var bindFocus = useFocusElement(isOpen).bind;
  var positionsStyle = React.useMemo(function () {
    return positions(theme);
  }, [theme]);
  var transitions = useTransition(isOpen, null, getTransitionForPosition(position));
  return jsx(React.Fragment, null, jsx(Overlay, {
    isOpen: isOpen,
    onRequestClose: closeOnClick ? onRequestClose : noop
  }, jsx(React.Fragment, null, transitions.map(function (_a) {
    var item = _a.item,
        key = _a.key,
        animationProps = _a.props,
        other = tslib_1.__rest(_a, ["item", "key", "props"]);

    return item && jsx(animated.div, tslib_1.__assign({
      key: key,
      role: role
    }, bindFocus, {
      tabIndex: -1,
      className: "Sheet",
      onClick: function onClick(e) {
        e.stopPropagation();
      },
      style: {
        transform: animationProps.transform
      },
      css: [{
        outline: "none",
        zIndex: theme.zIndices.modal,
        opacity: 1,
        position: "fixed"
      }, positionsStyle[position]]
    }, other, props), jsx(RequestCloseContext.Provider, {
      value: onRequestClose
    }, jsx(RemoveScroll, {
      forwardProps: true
    }, jsx("div", {
      className: "Sheet__container",
      css: {
        boxShadow: theme.shadows.md,
        background: theme.colors.background.layer,
        height: "100%"
      }
    }, children))));
  }))));
};
Sheet.propTypes = {
  isOpen: PropTypes.bool,
  onRequestClose: PropTypes.func,
  children: PropTypes.node,
  position: PropTypes.oneOf(["left", "top", "right", "bottom"]),
  closeOnClick: PropTypes.bool
};